import { useMemo, useState } from 'react';
import { Search, X, ChevronRight } from 'lucide-react';
import cipMajorsRaw from '../data/cip-majors.json';
import {
  cipFamily,
  cipFamilyLabel,
  CIP_FAMILY_LABELS,
} from '../lib/schoolDiscoveryTypes';
import type { CipMajor } from '../lib/schoolDiscoveryTypes';

// =============================================================================
// MajorPicker (v0.2.32)
//
// The major filter for School Discovery. Two ways to choose:
//   - Pick a broad 2-digit family ("Visual & Performing Arts").
//   - Type-ahead search the 421 specific 4-digit majors.
//
// A 4-digit selection takes precedence over a family selection (see the
// filter engine). Selecting either is optional — the filter is "use it or not".
// =============================================================================

const CIP_MAJORS = cipMajorsRaw as CipMajor[];

// Pre-clean titles: the federal data ends many with a period.
function cleanTitle(t: string): string {
  return t.replace(/\.$/, '');
}

interface Props {
  family: string | null;
  code: string | null;
  onChange: (next: { family: string | null; code: string | null }) => void;
}

export function MajorPicker({ family, code, onChange }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  // Families that actually have majors in the dataset, sorted by label.
  const families = useMemo(() => {
    const present = new Set(CIP_MAJORS.map((m) => cipFamily(m.code)));
    return Object.keys(CIP_FAMILY_LABELS)
      .filter((f) => present.has(f))
      .sort((a, b) => cipFamilyLabel(a).localeCompare(cipFamilyLabel(b)));
  }, []);

  // Type-ahead matches (cap to keep the list manageable).
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return CIP_MAJORS.filter((m) => cleanTitle(m.title).toLowerCase().includes(q)).slice(0, 30);
  }, [query]);

  const selectedMajor = code ? CIP_MAJORS.find((m) => m.code === code) : null;

  function pickCode(m: CipMajor) {
    onChange({ family: null, code: m.code });
    setQuery('');
    setOpen(false);
  }
  function pickFamily(f: string) {
    onChange({ family: f, code: null });
  }
  function clear() {
    onChange({ family: null, code: null });
    setQuery('');
  }

  return (
    <div className="space-y-2">
      {/* Current selection */}
      {(selectedMajor || family) && (
        <div className="flex items-center gap-2 rounded-md bg-forest-50 border border-forest-200 px-2 py-1.5">
          <span className="text-xs text-forest-800 flex-1">
            {selectedMajor ? (
              <>Major: <strong>{cleanTitle(selectedMajor.title)}</strong></>
            ) : (
              <>Field: <strong>{cipFamilyLabel(family!)}</strong></>
            )}
          </span>
          <button
            type="button"
            onClick={clear}
            className="text-forest-600 hover:text-forest-800"
            aria-label="Clear major filter"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Type-ahead search for a specific major */}
      <div className="relative">
        <div className="flex items-center gap-1.5 rounded-md border border-line px-2">
          <Search size={14} className="text-soft shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search a specific major…"
            className="w-full py-1.5 text-sm outline-none bg-transparent"
          />
        </div>
        {open && matches.length > 0 && (
          <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-md border border-line bg-paper shadow-lg">
            {matches.map((m) => (
              <li key={m.code}>
                <button
                  type="button"
                  onClick={() => pickCode(m)}
                  className="block w-full px-3 py-1.5 text-left text-sm hover:bg-forest-50"
                >
                  {cleanTitle(m.title)}
                  <span className="ml-1 text-xs text-soft">
                    · {cipFamilyLabel(cipFamily(m.code))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {open && query.trim() && matches.length === 0 && (
          <div className="absolute z-10 mt-1 w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-soft shadow-lg">
            No majors match "{query}".
          </div>
        )}
      </div>

      {/* Or pick a broad field */}
      {!selectedMajor && (
        <details className="text-sm">
          <summary className="cursor-pointer text-soft hover:text-ink">
            …or browse by broad field
          </summary>
          <ul className="mt-1 max-h-56 overflow-auto rounded-md border border-line">
            {families.map((f) => (
              <li key={f}>
                <button
                  type="button"
                  onClick={() => pickFamily(f)}
                  className={`flex w-full items-center gap-1 px-3 py-1.5 text-left text-sm hover:bg-forest-50 ${
                    family === f ? 'bg-forest-50 font-medium text-forest-800' : ''
                  }`}
                >
                  <ChevronRight size={12} className="text-line" />
                  {cipFamilyLabel(f)}
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
